import React, { useContext, useEffect, useState, useRef } from "react";
import usePlayerUtils from "../../hooks/usePlayerUtils";
import "./RadioPlayerMain.css";
import ProgressBar from "../ProgressBar/ProgressBar";
import { useAudioVisualizer } from "../../hooks/useAudioVisualizer";
import VolumeBar from "./VolumeBar";
import { AuthContext } from "../../services/AuthContext";
import usePlayerControls from "../../hooks/usePlayerControls";

export default function RadioPlayerHeader() {
  const {
    audioRef,
    currentTrack,
    nowPlaying,
    playbackState,
    setPlaybackState,
    detachHls,
    setCurrentTimeSec,
    playbackTimeMs,
    currentLiveMs,
    bufferStartMs,
    progressBarPercent,
    durationPercent,
    formatTimestamp,
    setManualTrack,
    setIsSeeking,
    parseDuration,
    setTimeShiftOffsetSec,
    timeShiftOffsetSec,
    elapsedTime
  } = usePlayerUtils();

  const {
    wrapChildClick,
    togglePlay,
  } = usePlayerControls({});

  const artist = currentTrack?.artist;
  const songTitle = currentTrack?.title;
  const songDuration = currentTrack?.duration
  const coverUrl = currentTrack?.coverUrl;

  const { isLoggedIn, setUserModalOpen } = useContext(AuthContext);
  const canvasRef = useAudioVisualizer(audioRef);
  const [volume, setVolume] = useState(1);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTime = () => setCurrentTimeSec(audio.currentTime || 0);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("seeked", onTime);
    audio.addEventListener("loadedmetadata", onTime);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("seeked", onTime);
      audio.removeEventListener("loadedmetadata", onTime);
    };
  }, []);

  const lastVolumeRef = useRef(1);

  const handleVolumeChange = (v) => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = v;
    }
    setVolume(v);
    if (v > 0) {
      lastVolumeRef.current = v;
    }
  };

  const toggleMute = () => {
    if (volume > 0) {
      handleVolumeChange(0);
    } else {
      handleVolumeChange(lastVolumeRef.current || 0.5);
    }
  };

  const stopPlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.pause();
    detachHls();
    setPlaybackState("stopped");
    setCurrentTimeSec(0);
    setTimeShiftOffsetSec(0);
  };

  const handleLiveBtn = (e) => {
    const audio = audioRef.current;
    if (!audio) return;

    handleResumeLive();
    setTimeShiftOffsetSec(0);

    if (playbackState !== "playing") {
      audio.play();
      setPlaybackState("playing");
    }

    audio.currentTime = timeShiftOffsetSec + audio.currentTime;
    setCurrentTimeSec(timeShiftOffsetSec + audio.currentTime);
  }

  const handleResumeLive = () => {
    setIsSeeking(false);
    setManualTrack(null);
  };

  const performSeek = (percent) => {
    // if (!isLoggedIn) {
    //   setUserModalOpen(true);
    //   return;
    // }

    const { maxTimeShiftSeconds, currentLiveTime, history } = nowPlaying || {};

    const audio = audioRef.current;
    if (!audio || !maxTimeShiftSeconds || !currentLiveTime || !history?.length) return;

    const currentLiveMs = new Date(currentLiveTime).getTime();
    const bufferStartMs = currentLiveMs - maxTimeShiftSeconds * 1000;
    const bufferEndMs = currentLiveMs;
    const desiredMs = bufferStartMs + percent * (bufferEndMs - bufferStartMs);

    const seekSec = (desiredMs - bufferStartMs) / 1000;
    const realCurrentTime = timeShiftOffsetSec + audio.currentTime;
    const currentSeekSec = realCurrentTime - maxTimeShiftSeconds + seekSec;

    const newOffset = realCurrentTime - currentSeekSec;
    setTimeShiftOffsetSec(newOffset);

    audio.currentTime = currentSeekSec;
    setCurrentTimeSec(currentSeekSec);

    const seekTimestamp = bufferStartMs + currentSeekSec * 1000;

    const currentSong = history.find(song => {
      const start = new Date(song.startedAt).getTime();
      const end = start + parseDuration(song.duration) * 1000;
      return seekTimestamp >= start && seekTimestamp < end;
    });

    if (currentSong) {
      setManualTrack(currentSong || null);
      setIsSeeking(true);
    }
  };

  const liveRadioProgress = () => {
    return (
      <div className="d-live-progress-content">
        <div className="d-live-progress-content-inner">
          <div className="d-live-progress-inner">
            {playerProgressInfo()}
          </div>
          {volumeBar()}
        </div>
      </div>
    );
  }

  const playerProgressInfo = () => {
    const liveBtnClassName = timeShiftOffsetSec <= 0 || playbackState === "stopped" ? "live-btn-disabled live-button" : "live-button";

    return (
      <div className="d-status-progerss">
        <button className={liveBtnClassName} onClick={wrapChildClick(() => handleLiveBtn())} disabled={timeShiftOffsetSec <= 0 || playbackState === "stopped"} >
          <div className="live-text">GO LIVE</div>
        </button>
        <div className="colapsed-times">
          <div className="d-time">{bufferStartMs ? formatTimestamp(bufferStartMs) : "--:--:--"}</div>
          <div className="d-time d-playbeck-time">
            <div>{playbackTimeMs ? new Date(playbackTimeMs).toLocaleTimeString("en-GB", { hour12: false }) : "--:--:--"}</div>
          </div>
          <div className="d-time">{currentLiveMs ? formatTimestamp(currentLiveMs) : "--:--:--"}</div>
        </div>
        <div className="colapsed-progress">
          <ProgressBar progressPercent={progressBarPercent / 100} onSeek={performSeek} />
        </div>
      </div>
    )
  }

  const playerControll = () => {
    const stopBtnClassName = playbackState === "stopped" ? "btn-disabled stop-btn" : "stop-btn";

    const modifiedCoverUrl = coverUrl?.includes("default")
        ? coverUrl.replace(".png", "-mid.png")
        : coverUrl;

    return (
      <div className="d-cover-info-btn">
        <div className="d-colapsed-info">
          <div className="d-colapsed-cover">
            <img className="d-colapsed-img-cover d-colapsed-fade" src={modifiedCoverUrl} alt="Cover" />
          </div>
          <div className="d-song-info">
            <div className="d-colapsed-song-info">
              <div className="d-song-title" title={songTitle}>{songTitle}</div>
              <div className="d-artist" title={artist}>{artist}</div>
            </div>
          </div>
        </div>
        <div className="d-track-info-main">
          <div className="d-track-btn-times">
            <div className="d-track-times">
              {(playbackState === "playing" || playbackState === "paused") &&
                <div className="d-player-indications">
                  <div className="d-player-duration">
                    <div>{elapsedTime()}</div>
                  </div>
                </div>
              }
            </div>
            <div className="d-track-btns">
              <div className="colapsed-btn d-end-btn-position">
                <div className="prev-for-btn">
                  <button>
                    <i className="fas fa-backward"></i>
                  </button>
                </div>
              </div>

              <div className="colapsed-btn d-end-btn-position">
                <div className="colapsed-player-btn">
                  <button className="play-btn" onClick={wrapChildClick(() => togglePlay())}>
                    {playbackState === "playing"
                      ? <i className="fas fa-pause"></i>
                      : <i className="fas fa-play"></i>
                    }
                  </button>
                </div>
              </div>
              <div className="colapsed-btn d-start-btn-position">
                <div className="stop-btn-ring" style={{ "--percent": "100%" }}>
                  <button className={stopBtnClassName} onClick={wrapChildClick(() => stopPlayback())} disabled={playbackState === "stopped"}>
                    <i className="fas fa-stop"></i>
                  </button>
                </div>
              </div>

              <div className="colapsed-btn d-start-btn-position">
                <div className="prev-for-btn">
                  <button>
                    <i className="fas fa-forward"></i>
                  </button>
                </div>
              </div>
            </div>
            <div className="d-track-times">
              {(playbackState === "playing" || playbackState === "paused") &&
                <div className="d-player-indications">
                  <div className="d-player-duration">
                    <div>{songDuration}</div>
                  </div>
                </div>
              }
            </div>
          </div>
          <div className="d-track-progress-times">
              <div className="progress-track">
                <div className="progress-playsholder"></div>
                {(playbackState === "playing" || playbackState === "paused") &&
                <div
                  className="progress-filled"
                  style={{
                    width: `${durationPercent() * 100}%`,
                    backgroundColor: playbackState === "playing"
                      ? "rgb(179, 177, 177)"
                      : "rgb(127, 127, 127)",
                  }}
                ></div>}
              </div>
          </div>
        </div>
        <div className="d-track-placeholder"></div>
      </div>
    )
  }

  const volumeBar = () => {
    return (
      <div className="d-volume-section">
        <div className="d-volume-inner">
          <i
            className={`fas ${volume < 0.01 ? 'fa-volume-mute' : volume < 0.5 ? 'fa-volume-down' : 'fa-volume-up'}`}
            onClick={toggleMute}
          ></i>
          <div className="d-volume-bar-content">
            <VolumeBar value={volume} onChange={handleVolumeChange} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="d-live-progress">
        {liveRadioProgress()}
      </div>
      <div className="radio-player-main">
        <div className="player-main">
          {playerControll()}
        </div>
      </div>
    </>
  );
}
